/**
 * Tab (pestaña) → tree of content nodes: optional depth (block / theme / unit…).
 * Pure helpers; UI strings live in components.
 */

import {
  applyContenidoItemsToNode,
  archivoOrdenKey,
  contenidoOrdenFromItems,
  listContenidoItems,
  moveContenidoItemBySource,
  sanitizeContenidoOrdenEntries,
  syncContenidoOrdenOnNode,
} from "./contenidoOrden";

/** @typedef {{ nombre: string, tipo?: string, url?: string, storageKey?: string }} TemarioArchivo */
/** @typedef {{ titulo: string, bloque: string }} TemarioTestRef */
/** @typedef {import("./contenidoOrden").ContenidoOrdenEntry} ContenidoOrdenEntry */
/**
 * @typedef {object} TemarioNodo
 * @property {string} [id]
 * @property {string} nombre
 * @property {TemarioArchivo[]} archivos
 * @property {TemarioTestRef[]} testRefs
 * @property {ContenidoOrdenEntry[]} contenidoOrden
 * @property {TemarioNodo[]} hijos
 */

export const MAX_TEMARIO_DEPTH = 12;

/**
 * @param {number[]} path
 * @returns {string}
 */
export function pathKeyFromIndices(path) {
  if (!Array.isArray(path) || path.length === 0) {
    return "";
  }
  return path.join(",");
}

/**
 * @param {string} key
 * @returns {number[]}
 */
export function pathIndicesFromKey(key) {
  if (typeof key !== "string" || key.trim() === "") {
    return [];
  }
  return key
    .split(",")
    .map((s) => Number.parseInt(s, 10))
    .filter((n) => Number.isFinite(n) && n >= 0);
}

/**
 * @param {TemarioNodo[]} nodes
 * @param {number[]} path
 * @returns {TemarioNodo | null}
 */
export function getNodeAtPath(nodes, path) {
  if (!Array.isArray(nodes) || !Array.isArray(path)) {
    return null;
  }
  let list = nodes;
  let current = null;
  for (let d = 0; d < path.length; d++) {
    const i = path[d];
    if (typeof i !== "number" || i < 0 || i >= list.length) {
      return null;
    }
    current = list[i];
    list = Array.isArray(current.hijos) ? current.hijos : [];
  }
  return current;
}

/**
 * Appends a file to archivos. Existing archivos and testRefs stay in their current order.
 * The new file is recorded at the end of contenidoOrden, or at the start when posicion is "encima".
 * @param {TemarioNodo[]} nodes
 * @param {number[]} path
 * @param {"debajo" | "encima"} posicion
 * @param {TemarioArchivo} archivo
 * @returns {TemarioNodo[] | null}
 */
export function addArchivoAtPath(nodes, path, archivo, posicion) {
  const target = getNodeAtPath(nodes, path);
  if (!target || !archivo || typeof archivo.nombre !== "string") {
    return null;
  }
  const clone = cloneTemarioLista(nodes);
  const mutable = getNodeAtPath(clone, path);
  if (!mutable) {
    return null;
  }
  const archivos = Array.isArray(mutable.archivos) ? [...mutable.archivos] : [];
  if (archivos.some((a) => a && a.nombre === archivo.nombre)) {
    return null;
  }
  const current = contenidoOrdenFromItems(listContenidoItems(mutable));
  mutable.archivos = [...archivos, archivo];
  const nombre = archivoOrdenKey(archivo);
  mutable.contenidoOrden = nombre
    ? sequenceWithNewEntry(current, { kind: "archivo", nombre }, posicion)
    : current;
  return clone;
}

/**
 * Removes one archivo entry from a node by index (does not delete storage objects).
 * @param {TemarioNodo[]} nodes
 * @param {number[]} path
 * @param {number} archivoIndex
 * @returns {TemarioNodo[] | null}
 */
export function removeArchivoAtPath(nodes, path, archivoIndex) {
  if (!Array.isArray(nodes) || !Array.isArray(path)) {
    return null;
  }
  if (typeof archivoIndex !== "number" || archivoIndex < 0 || !Number.isInteger(archivoIndex)) {
    return null;
  }
  const clone = cloneTemarioLista(nodes);
  const mutable = getNodeAtPath(clone, path);
  if (!mutable || !Array.isArray(mutable.archivos)) {
    return null;
  }
  if (archivoIndex >= mutable.archivos.length) {
    return null;
  }
  applyContenidoItemsToNode(
    mutable,
    listContenidoItems(mutable).filter(
      (item) => !(item.kind === "archivo" && item.sourceIndex === archivoIndex),
    ),
  );
  return clone;
}

/**
 * Moves one file or test one step in the node's shared sequence.
 * Only contenidoOrden changes; archivos and testRefs keep their order.
 * @param {TemarioNodo[]} nodes
 * @param {number[]} path
 * @param {"archivo" | "test"} kind
 * @param {number} sourceIndex
 * @param {number} delta
 * @returns {TemarioNodo[] | null}
 */
export function moveContenidoItemAtPath(nodes, path, kind, sourceIndex, delta) {
  if (
    !Array.isArray(nodes) ||
    !Array.isArray(path) ||
    (kind !== "archivo" && kind !== "test") ||
    typeof sourceIndex !== "number" ||
    !Number.isInteger(sourceIndex) ||
    typeof delta !== "number" ||
    !Number.isInteger(delta) ||
    delta === 0
  ) {
    return null;
  }
  const clone = cloneTemarioLista(nodes);
  const mutable = getNodeAtPath(clone, path);
  const sources = kind === "archivo" ? mutable?.archivos : mutable?.testRefs;
  if (!mutable || !Array.isArray(sources) || sourceIndex < 0 || sourceIndex >= sources.length) {
    return null;
  }
  if (!moveContenidoItemBySource(mutable, kind, sourceIndex, delta)) {
    return null;
  }
  return clone;
}

/**
 * @param {TemarioNodo[]} nodes
 * @param {number[]} path
 * @param {number} archivoIndex
 * @param {number} delta
 * @returns {TemarioNodo[] | null}
 */
export function moveArchivoAtPath(nodes, path, archivoIndex, delta) {
  return moveContenidoItemAtPath(nodes, path, "archivo", archivoIndex, delta);
}

/**
 * Places a new sequence entry at one end without reordering the current entries.
 * @param {ContenidoOrdenEntry[]} current
 * @param {ContenidoOrdenEntry} entry
 * @param {"debajo" | "encima"} [posicion]
 * @returns {ContenidoOrdenEntry[]}
 */
function sequenceWithNewEntry(current, entry, posicion) {
  return posicion === "encima" ? [entry, ...current] : [...current, entry];
}

/**
 * @param {TemarioNodo[]} nodes
 * @param {number[]} parentPath
 * @param {string} nombre
 * @returns {TemarioNodo[] | null}
 */
export function insertChildAtPath(nodes, parentPath, nombre) {
  const trimmed = (nombre ?? "").trim();
  if (trimmed === "") {
    return null;
  }
  const child = {
    id: createSectionId(),
    nombre: trimmed,
    archivos: [],
    testRefs: [],
    contenidoOrden: [],
    hijos: [],
  };
  const clone = cloneTemarioLista(nodes);
  if (parentPath.length === 0) {
    clone.push(child);
    return clone;
  }
  const parent = getNodeAtPath(clone, parentPath);
  if (!parent) {
    return null;
  }
  if (!Array.isArray(parent.hijos)) {
    parent.hijos = [];
  }
  parent.hijos.push(child);
  return clone;
}

/**
 * Creates a child under parentPath with the given name and first archivo.
 * @param {TemarioNodo[]} nodes
 * @param {number[]} parentPath
 * @param {string} nombre
 * @param {TemarioArchivo} archivo
 * @param {"debajo" | "encima"} posicion
 * @returns {TemarioNodo[] | null}
 */
export function insertNodeWithArchivoAtParent(
  nodes,
  parentPath,
  nombre,
  archivo,
  posicion,
) {
  const withNode = insertChildAtPath(nodes, parentPath, nombre);
  if (!withNode) {
    return null;
  }
  /** @type {number[]} */
  let childPath;
  if (parentPath.length === 0) {
    childPath = [withNode.length - 1];
  } else {
    const p = getNodeAtPath(withNode, parentPath);
    if (!p || !Array.isArray(p.hijos) || p.hijos.length === 0) {
      return null;
    }
    childPath = [...parentPath, p.hijos.length - 1];
  }
  return addArchivoAtPath(withNode, childPath, archivo, posicion);
}

/**
 * @param {TemarioNodo[]} nodes
 * @param {number[]} path
 * @param {string} nombre
 * @returns {TemarioNodo[] | null}
 */
export function updateNodeNameAtPath(nodes, path, nombre) {
  const trimmed = (nombre ?? "").trim();
  if (trimmed === "") {
    return null;
  }
  const clone = cloneTemarioLista(nodes);
  const node = getNodeAtPath(clone, path);
  if (!node) {
    return null;
  }
  node.nombre = trimmed;
  return clone;
}

/**
 * @param {TemarioNodo[]} nodes
 * @param {number[]} path
 * @returns {TemarioNodo[] | null}
 */
export function removeNodeAtPath(nodes, path) {
  if (!Array.isArray(path) || path.length === 0) {
    return null;
  }
  const clone = cloneTemarioLista(nodes);
  const parentPath = path.slice(0, -1);
  const idx = path[path.length - 1];
  if (typeof idx !== "number" || idx < 0) {
    return null;
  }
  if (parentPath.length === 0) {
    if (idx >= clone.length) {
      return null;
    }
    clone.splice(idx, 1);
    return clone;
  }
  const parent = getNodeAtPath(clone, parentPath);
  if (!parent || !Array.isArray(parent.hijos) || idx >= parent.hijos.length) {
    return null;
  }
  parent.hijos.splice(idx, 1);
  return clone;
}

/**
 * @param {TemarioNodo[]} nodes
 * @param {number[]} path
 * @param {number} delta -1 | 1
 * @returns {TemarioNodo[] | null}
 */
export function moveNodeAtPath(nodes, path, delta) {
  if (delta !== -1 && delta !== 1) {
    return null;
  }
  if (!Array.isArray(path) || path.length === 0) {
    return null;
  }
  const clone = cloneTemarioLista(nodes);
  const parentPath = path.slice(0, -1);
  const idx = path[path.length - 1];
  const newIdx = idx + delta;
  const siblings =
    parentPath.length === 0 ? clone : getNodeAtPath(clone, parentPath)?.hijos;
  if (!Array.isArray(siblings) || newIdx < 0 || newIdx >= siblings.length) {
    return null;
  }
  const tmp = siblings[idx];
  siblings[idx] = siblings[newIdx];
  siblings[newIdx] = tmp;
  return clone;
}

/**
 * @param {TemarioNodo[]} nodes - Current level (or full tree on the first call).
 * @param {string[]} depthLabels
 * @param {number[]} prefixPath
 * @param {TemarioNodo[] | null} [rootForTitles] - Root list for titlesAlongPath; omit on first call.
 * @returns {{ pathKey: string, label: string, depth: number }[]}
 */
export function listPathOptions(nodes, depthLabels, prefixPath = [], rootForTitles = null) {
  const labels = Array.isArray(depthLabels) && depthLabels.length > 0
    ? depthLabels
    : ["Block", "Topic", "Unit"];
  /** @type {{ pathKey: string, label: string, depth: number }[]} */
  const out = [];
  if (!Array.isArray(nodes)) {
    return out;
  }
  const root = rootForTitles == null ? nodes : rootForTitles;
  for (let i = 0; i < nodes.length; i++) {
    const path = [...prefixPath, i];
    const pathKey = pathKeyFromIndices(path);
    const titles = titlesAlongPath(root, path);
    const label = titles.join(" / ");
    const depth = path.length - 1;
    out.push({
      pathKey,
      label: label || `${labels[Math.min(depth, labels.length - 1)]} ${i + 1}`,
      depth,
    });
    const n = nodes[i];
    const hijos = Array.isArray(n.hijos) ? n.hijos : [];
    if (hijos.length > 0) {
      out.push(...listPathOptions(hijos, labels, path, root));
    }
  }
  return out;
}

/**
 * @param {TemarioNodo[]} rootNodes
 * @param {number[]} path
 * @returns {string[]}
 */
export function titlesAlongPath(rootNodes, path) {
  const titles = [];
  let list = rootNodes;
  for (let d = 0; d < path.length; d++) {
    const i = path[d];
    if (!Array.isArray(list) || i < 0 || i >= list.length) {
      break;
    }
    const node = list[i];
    titles.push((node.nombre ?? "").trim() || "—");
    list = Array.isArray(node.hijos) ? node.hijos : [];
  }
  return titles;
}

/**
 * @param {unknown} raw
 * @param {number} depth
 * @returns {TemarioNodo | null}
 */
const SECTION_ID_PATTERN = /^[A-Za-z0-9_-]{1,80}$/;
const UNSAFE_SECTION_IDS = new Set(["__proto__", "constructor", "prototype"]);

/**
 * @returns {string}
 */
function createSectionId() {
  const bytes = new Uint8Array(12);
  const fillRandom = globalThis.crypto?.getRandomValues?.bind(globalThis.crypto);
  if (fillRandom) {
    fillRandom(bytes);
  } else {
    for (let index = 0; index < bytes.length; index += 1) {
      bytes[index] = Math.floor(Math.random() * 256);
    }
  }
  const hex = Array.from(bytes, (value) => value.toString(16).padStart(2, "0")).join("");
  return `sec_${hex}`;
}

/**
 * @param {unknown} rawId
 * @returns {string}
 */
function sectionIdFromRaw(rawId) {
  const id = typeof rawId === "string" ? rawId.trim() : "";
  if (SECTION_ID_PATTERN.test(id) && !UNSAFE_SECTION_IDS.has(id)) {
    return id;
  }
  return createSectionId();
}

export function normalizeTemarioNode(raw, depth = 0) {
  if (depth > MAX_TEMARIO_DEPTH) {
    return null;
  }
  if (!raw || typeof raw !== "object") {
    return null;
  }
  const rec = /** @type {Record<string, unknown>} */ (raw);
  const nombre = typeof rec.nombre === "string" ? rec.nombre.trim() : "";
  const archivos = Array.isArray(rec.archivos)
    ? rec.archivos
        .filter((a) => a && typeof a === "object" && typeof a.nombre === "string")
        .map((a) => {
          const ar = /** @type {Record<string, unknown>} */ (a);
          return {
            nombre: String(ar.nombre).trim(),
            tipo: typeof ar.tipo === "string" ? ar.tipo : "",
            url: typeof ar.url === "string" ? ar.url : "",
            storageKey: typeof ar.storageKey === "string" ? ar.storageKey : "",
          };
        })
        .filter((a) => a.nombre.length > 0)
    : [];
  const testRefsRaw = Array.isArray(rec.testRefs) ? rec.testRefs : [];
  /** @type {TemarioTestRef[]} */
  const testRefs = [];
  for (const ref of testRefsRaw) {
    if (!ref || typeof ref !== "object") {
      continue;
    }
    const rr = /** @type {Record<string, unknown>} */ (ref);
    const titulo = typeof rr.titulo === "string" ? rr.titulo.trim() : "";
    const bloque = typeof rr.bloque === "string" ? rr.bloque.trim() : "";
    if (titulo.length > 0) {
      testRefs.push({ titulo, bloque });
    }
  }
  const hijosRaw = Array.isArray(rec.hijos) ? rec.hijos : [];
  /** @type {TemarioNodo[]} */
  const hijos = [];
  for (const h of hijosRaw) {
    const n = normalizeTemarioNode(h, depth + 1);
    if (n) {
      hijos.push(n);
    }
  }
  const keep =
    nombre.length > 0 || archivos.length > 0 || hijos.length > 0 || testRefs.length > 0;
  if (!keep) {
    return null;
  }
  const node = {
    id: sectionIdFromRaw(rec.id),
    nombre,
    archivos,
    testRefs,
    contenidoOrden: sanitizeContenidoOrdenEntries(rec.contenidoOrden),
    hijos,
  };
  syncContenidoOrdenOnNode(node);
  return node;
}

/**
 * Appends a test ref. Existing archivos and testRefs stay in their current order,
 * and the new test is recorded at the end of contenidoOrden.
 * @param {TemarioNodo[]} nodes
 * @param {number[]} path
 * @param {{ titulo: string, bloque: string }} testRef
 * @returns {TemarioNodo[] | null}
 */
export function addTestRefAtPath(nodes, path, testRef) {
  const target = getNodeAtPath(nodes, path);
  if (!target || !testRef || typeof testRef.titulo !== "string" || testRef.titulo.trim() === "") {
    return null;
  }
  const clone = cloneTemarioLista(nodes);
  const mutable = getNodeAtPath(clone, path);
  if (!mutable) {
    return null;
  }
  const refs = Array.isArray(mutable.testRefs) ? mutable.testRefs : [];
  const titulo = testRef.titulo.trim();
  const bloque = typeof testRef.bloque === "string" ? testRef.bloque.trim() : "";
  if (refs.some((r) => r && r.titulo === titulo)) {
    return null;
  }
  const current = contenidoOrdenFromItems(listContenidoItems(mutable));
  mutable.testRefs = [...refs, { titulo, bloque }];
  mutable.contenidoOrden = [...current, { kind: "test", titulo }];
  return clone;
}

/**
 * @param {TemarioNodo[] | null | undefined} nodes
 * @returns {Set<string>}
 */
export function collectLinkedTestTitles(nodes) {
  const titles = new Set();
  const walk = (list) => {
    for (const node of Array.isArray(list) ? list : []) {
      for (const ref of Array.isArray(node?.testRefs) ? node.testRefs : []) {
        const titulo = typeof ref?.titulo === "string" ? ref.titulo.trim() : "";
        if (titulo) {
          titles.add(titulo);
        }
      }
      walk(node?.hijos);
    }
  };
  walk(nodes);
  return titles;
}

/**
 * @param {TemarioNodo[]} nodes
 * @param {number[]} path
 * @param {number} refIndex
 * @returns {TemarioNodo[] | null}
 */
export function removeTestRefAtPath(nodes, path, refIndex) {
  if (
    !Array.isArray(nodes) ||
    !Array.isArray(path) ||
    typeof refIndex !== "number" ||
    !Number.isInteger(refIndex)
  ) {
    return null;
  }
  const clone = cloneTemarioLista(nodes);
  const mutable = getNodeAtPath(clone, path);
  if (!mutable || !Array.isArray(mutable.testRefs) || refIndex < 0 || refIndex >= mutable.testRefs.length) {
    return null;
  }
  applyContenidoItemsToNode(
    mutable,
    listContenidoItems(mutable).filter(
      (item) => !(item.kind === "test" && item.sourceIndex === refIndex),
    ),
  );
  return clone;
}

/**
 * @param {TemarioNodo[]} nodes
 * @param {number[]} path
 * @param {number} refIndex
 * @param {number} delta
 * @returns {TemarioNodo[] | null}
 */
export function moveTestRefAtPath(nodes, path, refIndex, delta) {
  return moveContenidoItemAtPath(nodes, path, "test", refIndex, delta);
}

/**
 * @param {unknown} arr
 * @returns {TemarioNodo[]}
 */
export function normalizeTemarioLista(arr) {
  if (!Array.isArray(arr)) {
    return [];
  }
  /** @type {TemarioNodo[]} */
  const out = [];
  for (const item of arr) {
    const n = normalizeTemarioNode(item, 0);
    if (n) {
      out.push(n);
    }
  }
  return out;
}

/**
 * @param {unknown} contenidoSrc
 * @returns {Record<string, TemarioNodo[]>}
 */
export function normalizeCourseContenidoDeep(contenidoSrc) {
  if (!contenidoSrc || typeof contenidoSrc !== "object") {
    return {};
  }
  /** @type {Record<string, TemarioNodo[]>} */
  const out = {};
  for (const [k, v] of Object.entries(
    /** @type {Record<string, unknown>} */ (contenidoSrc),
  )) {
    const key = (k ?? "").trim().replace(/\s+/g, " ");
    if (!key) {
      continue;
    }
    const list = normalizeTemarioLista(v);
    const previous = out[key];
    if (!Array.isArray(previous) || previous.length === 0) {
      out[key] = list;
      continue;
    }
    if (list.length > 0) {
      out[key] = [...previous, ...list];
    }
  }
  return out;
}

/**
 * @param {TemarioNodo[]} nodes
 * @returns {TemarioNodo[]}
 */
export function cloneTemarioLista(nodes) {
  return JSON.parse(JSON.stringify(nodes));
}

/**
 * @param {TemarioNodo[]} nodos
 * @returns {{ nodeCount: number, archivoCount: number }}
 */
export function countTemarioStats(nodos) {
  if (!Array.isArray(nodos) || nodos.length === 0) {
    return { nodeCount: 0, archivoCount: 0 };
  }
  let nodeCount = 0;
  let archivoCount = 0;
  const walk = (arr) => {
    for (const n of arr) {
      if (!n) {
        continue;
      }
      nodeCount += 1;
      archivoCount += Array.isArray(n.archivos) ? n.archivos.length : 0;
      if (Array.isArray(n.hijos) && n.hijos.length > 0) {
        walk(n.hijos);
      }
    }
  };
  walk(nodos);
  return { nodeCount, archivoCount };
}

/**
 * Strip transient fields before API persist (e.g. accidental contenido base64).
 * @param {Record<string, TemarioNodo[]>} contenido
 */
export function stripArchivoTransientFieldsDeep(contenido) {
  if (!contenido || typeof contenido !== "object") {
    return;
  }
  for (const key of Object.keys(contenido)) {
    const list = contenido[key];
    if (Array.isArray(list)) {
      walkStripArchivos(list);
    }
  }
}

/**
 * @param {TemarioNodo[]} nodes
 */
function walkStripArchivos(nodes) {
  for (const node of nodes) {
    if (!node || typeof node !== "object") {
      continue;
    }
    if (Array.isArray(node.archivos)) {
      for (const a of node.archivos) {
        if (a && typeof a === "object" && "contenido" in a) {
          delete a.contenido;
        }
      }
    }
    if (Array.isArray(node.hijos)) {
      walkStripArchivos(node.hijos);
    }
    if (Array.isArray(node.testRefs)) {
      for (const ref of node.testRefs) {
        if (ref && typeof ref === "object" && "contenido" in ref) {
          delete ref.contenido;
        }
      }
    }
  }
}
